/**
 * Opens allocation/rebalance sell recommendation sheets from R2D multi-rec.
 *
 * Belongs here: R2D entry that fetches plans summary CTA + opens sell sheet nested.
 * Does not belong here: plans-list open path (useOpenPlanRecommendationBottomSheet).
 */

import { useCallback } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { useBottomSheet } from '../../state-management/contexts/BottomSheetContext'
import { useLoading } from '../../state-management/contexts/LoadingContext'
import { lightImpactVibrate } from '../../utils/appBridge'
import { LumpsumRecommendationActionType } from '../../features/allocation/plans-v4/lumpsum/api/common.types'
import {
  fetchLumpsumAllocationPlans,
  fetchLumpsumRebalancePlans
} from '../../features/allocation/plans-v4/lumpsum/api/repository'
import { LUMPSUM_PLANS_API_RETRY_COUNT } from '../../features/allocation/plans-v4/lumpsum/api/queryOptions'
import { PlansPlanMode } from '../../features/allocation/plans-v4/lumpsum/api/types'
import {
  fetchLumpsumAllocationPlanRecommendation,
  fetchLumpsumRebalancePlanRecommendation
} from '../../features/allocation/plans-v4/lumpsum/plan-bottom-sheet/api/repository'
import { AllocationSellPlanRecommendationContainer } from '../../features/allocation/plans-v4/lumpsum/plan-bottom-sheet/allocation/sell/AllocationSellPlanRecommendationContainer'
import { RebalanceSellPlanRecommendationContainer } from '../../features/allocation/plans-v4/lumpsum/plan-bottom-sheet/rebalance/sell/RebalanceSellPlanRecommendationContainer'
import {
  FundMissedGainsInfoBottomSheet,
  type FundMissedGainsInfoParams
} from '../../features/allocation/plans-v4/lumpsum/plan-bottom-sheet/shared/missed-gains/FundMissedGainsInfoBottomSheet'
import { PlanRecommendationSheetErrorState } from '../../features/allocation/plans-v4/lumpsum/plan-bottom-sheet/shared/sheet/PlanRecommendationSheetErrorState'

/** Same keys as LumpsumPlansProvider so CTA shares cache with plans list. */
const LUMPSUM_ALLOCATION_PLANS_QUERY_KEY = 'lumpsum-plans-v4-allocation'
const LUMPSUM_REBALANCE_PLANS_QUERY_KEY = 'lumpsum-plans-v4-rebalance'

/** Same keys as sell containers so the sheet reuses the prefetched recommendation. */
const ALLOCATION_SELL_PLAN_RECOMMENDATION_QUERY_KEY = 'lumpsum-plans-v4-allocation-sell-recommendation'
const REBALANCE_SELL_PLAN_RECOMMENDATION_QUERY_KEY = 'lumpsum-plans-v4-rebalance-sell-recommendation'

const ALLOCATION_FUND_UNAVAILABLE_DESCRIPTION =
  "We couldn't open a sell recommendation for this fund in allocation."
const REBALANCE_FUND_UNAVAILABLE_DESCRIPTION =
  "We couldn't open a sell recommendation for this fund in rebalance."

type R2dPlanSellMode = 'allocation' | 'rebalance'

function isUsableSellRecommendation(details: unknown): boolean {
  if (details == null || typeof details !== 'object') return false
  if (!('fundsToBeBought' in details)) return false
  const fundName = (details as { fundName?: string | null }).fundName
  return typeof fundName === 'string' && fundName.trim().length > 0
}

/**
 * Plug-and-play opener for R2D multi-rec → nested sell recommendation sheets.
 * Always opens SELL. Multi-rec stays on the main sheet.
 */
export function useOpenR2dPlanSellRecommendationSheet() {
  const queryClient = useQueryClient()
  const { showLoading, hideLoading } = useLoading()
  const { openNestedBottomSheet, closeNestedBottomSheet } = useBottomSheet()

  const openSellRecommendationSheet = useCallback(
    async (planMode: R2dPlanSellMode, isin: string) => {
      const fundIsin = isin?.trim() ?? ''
      if (!fundIsin) return

      const sheetsPlanMode =
        planMode === 'allocation' ? PlansPlanMode.ALLOCATION : PlansPlanMode.REBALANCE

      const openErrorSheet = (description?: string) => {
        openNestedBottomSheet(
          <PlanRecommendationSheetErrorState
            planMode={sheetsPlanMode}
            fundIsin={fundIsin}
            description={description}
            onDismiss={closeNestedBottomSheet}
            onRetry={() => {
              void openSellRecommendationSheet(planMode, fundIsin)
            }}
          />
        )
      }

      showLoading()
      try {
        let sellDetails: unknown
        try {
          if (planMode === 'allocation') {
            sellDetails = await queryClient.fetchQuery({
              queryKey: [ALLOCATION_SELL_PLAN_RECOMMENDATION_QUERY_KEY, fundIsin],
              queryFn: () =>
                fetchLumpsumAllocationPlanRecommendation(
                  fundIsin,
                  LumpsumRecommendationActionType.SELL
                ),
              retry: LUMPSUM_PLANS_API_RETRY_COUNT
            })
          } else {
            sellDetails = await queryClient.fetchQuery({
              queryKey: [REBALANCE_SELL_PLAN_RECOMMENDATION_QUERY_KEY, fundIsin],
              queryFn: () =>
                fetchLumpsumRebalancePlanRecommendation(
                  fundIsin,
                  LumpsumRecommendationActionType.SELL
                ),
              retry: LUMPSUM_PLANS_API_RETRY_COUNT
            })
          }
        } catch {
          openErrorSheet()
          return
        }

        if (!isUsableSellRecommendation(sellDetails)) {
          openErrorSheet(
            planMode === 'allocation'
              ? ALLOCATION_FUND_UNAVAILABLE_DESCRIPTION
              : REBALANCE_FUND_UNAVAILABLE_DESCRIPTION
          )
          return
        }

        // Summary CTA is best-effort — sheet can still open if it fails
        let transactionCta: unknown
        try {
          if (planMode === 'allocation') {
            transactionCta = (
              await queryClient.fetchQuery({
                queryKey: [LUMPSUM_ALLOCATION_PLANS_QUERY_KEY],
                queryFn: fetchLumpsumAllocationPlans,
                retry: LUMPSUM_PLANS_API_RETRY_COUNT
              })
            )?.transactionCta
          } else {
            transactionCta = (
              await queryClient.fetchQuery({
                queryKey: [LUMPSUM_REBALANCE_PLANS_QUERY_KEY],
                queryFn: fetchLumpsumRebalancePlans,
                retry: LUMPSUM_PLANS_API_RETRY_COUNT
              })
            )?.transactionCta
          }
        } catch {
          transactionCta = undefined
        }

        const openSellSheet = () => {
          const onMissedGainsInfoClick = (params: FundMissedGainsInfoParams) => {
            // Single nested slot: replace sell with missed-gains, restore sell on dismiss
            openNestedBottomSheet(
              <FundMissedGainsInfoBottomSheet
                onDismiss={() => {
                  closeNestedBottomSheet()
                  openSellSheet()
                }}
                missedGains={params.missedGains}
                currentValue={params.currentValue}
                planSnapshotDate={params.planSnapshotDate}
              />
            )
          }

          const containerProps = {
            onDismiss: closeNestedBottomSheet,
            isin: fundIsin,
            onMissedGainsInfoClick,
            transactionCta
          }

          if (planMode === 'allocation') {
            openNestedBottomSheet(<AllocationSellPlanRecommendationContainer {...containerProps} />)
            return
          }
          openNestedBottomSheet(<RebalanceSellPlanRecommendationContainer {...containerProps} />)
        }

        lightImpactVibrate()
        openSellSheet()
      } finally {
        hideLoading()
      }
    },
    [closeNestedBottomSheet, hideLoading, openNestedBottomSheet, queryClient, showLoading]
  )

  const openAllocationSellRecommendationSheet = useCallback(
    (isin: string) => {
      void openSellRecommendationSheet('allocation', isin)
    },
    [openSellRecommendationSheet]
  )

  const openRebalanceSellRecommendationSheet = useCallback(
    (isin: string) => {
      void openSellRecommendationSheet('rebalance', isin)
    },
    [openSellRecommendationSheet]
  )

  return {
    openAllocationSellRecommendationSheet,
    openRebalanceSellRecommendationSheet
  }
}
